import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import { toast } from "react-toastify";

import { NURSE_COLORS } from "../../../nurse-dashboard/theme";
import {
  approveTransferRequest,
  cancelTransferRequest,
  describeBedCharge,
  executeTransferRequest,
  getTransferRequests,
  rejectTransferRequest,
} from "../../api/bedTransferRequestApi";
import { raiseOccupancyApproval } from "../../api/occupancyApprovalApi";
import { describeAccrual, moveBedAccrual } from "../../api/autoChargeApi";
import RaiseTransferRequestModal from "./RaiseTransferRequestModal";
import { formatIstDateTime } from "../../../../utils/istDateTime";

const STATUS_COLORS = {
  raised: "warning",
  approved: "info",
  transferred: "success",
  rejected: "error",
  cancelled: "default",
};

function fmt(dt) {
  return formatIstDateTime(dt, "-");
}

export default function BedTransferRequisitionList() {
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [raiseOpen, setRaiseOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [approvalNeeded, setApprovalNeeded] = useState(null); // { requisitionId, admissionId, bedCode }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getTransferRequests(statusFilter ? { status: statusFilter } : {});
      setRows(data);
    } catch (err) {
      console.error("Failed to load transfer requests:", err);
      toast.error("Failed to load transfer requests.");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const runAction = async (id, fn, successMessage) => {
    setBusyId(id);
    try {
      const data = await fn(id);
      if (data?.status === "Success") {
        toast.success(data.message || successMessage);
        load();
      } else {
        toast.error(data?.message || "Action failed.");
      }
    } catch (err) {
      console.error("Transfer request action failed:", err);
      toast.error(err?.response?.data?.message || "Action failed.");
    } finally {
      setBusyId(null);
    }
  };

  // Execute is special-cased: a bed flagged is_approval_required with no live
  // approval fails with requires_approval/bed_code (same shape BedAllocation.js
  // already handles from execute_bed_swap()) — offer the one-click raise
  // instead of a dead-end toast.
  const runExecute = async (r) => {
    setBusyId(r.id);
    try {
      const data = await executeTransferRequest(r.id);
      if (data?.status === "Success") {
        toast.success(data.message || "Requisition executed.");
        const charge = describeBedCharge(data);
        if (charge) {
          // Warnings mean the bed moved but the charge didn't post — hold it on
          // screen until dismissed rather than letting revenue leak past a
          // 5-second toast.
          toast[charge.severity](charge.text, {
            autoClose: charge.severity === "warning" ? false : undefined,
          });
        }
        // Follow the recurring bed-day accrual to the new bed. The start
        // reprices off the admission's current bed, which the swap has already
        // updated, so the new service carries the destination bed's rate.
        const accrual = describeAccrual(await moveBedAccrual(r.admission_id));
        if (accrual) {
          toast[accrual.severity](accrual.text, {
            autoClose: accrual.severity === "warning" ? false : undefined,
          });
        }
        load();
      } else if (data?.requires_approval) {
        setApprovalNeeded({
          requisitionId: r.id,
          admissionId: r.admission_id,
          bedCode: data.bed_code || r.to_bed_code,
        });
        toast.warning(data.message || "This bed requires supervisor approval.");
      } else {
        toast.error(data?.message || "Action failed.");
      }
    } catch (err) {
      console.error("Execute transfer request failed:", err);
      toast.error(err?.response?.data?.message || "Action failed.");
    } finally {
      setBusyId(null);
    }
  };

  const handleRaiseApproval = async () => {
    if (!approvalNeeded) return;
    try {
      const data = await raiseOccupancyApproval({
        admissionId: approvalNeeded.admissionId,
        bedCode: approvalNeeded.bedCode,
      });
      if (data?.status === "Success") {
        toast.success(data.message || "Approval request raised. A supervisor must approve it from the Approvals Center before this bed can be allocated.");
      } else {
        toast.error(data?.message || "Could not raise the approval request.");
      }
    } catch (err) {
      console.error("Raise occupancy approval failed:", err);
      toast.error(err?.response?.data?.message || "Could not raise the approval request.");
    } finally {
      setApprovalNeeded(null);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error("A rejection reason is required.");
      return;
    }
    setBusyId(rejectTarget);
    try {
      const data = await rejectTransferRequest(rejectTarget, rejectReason.trim());
      if (data?.status === "Success") {
        toast.success(data.message || "Requisition rejected.");
        setRejectTarget(null);
        setRejectReason("");
        load();
      } else {
        toast.error(data?.message || "Could not reject the requisition.");
      }
    } catch (err) {
      console.error("Reject transfer request failed:", err);
      toast.error(err?.response?.data?.message || "Could not reject the requisition.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Box sx={{ bgcolor: NURSE_COLORS.pageBg, minHeight: "100vh", p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6" color={NURSE_COLORS.textPrimary}>
          Bed Transfer Requisitions
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={() => setRaiseOpen(true)}
          >
            Raise Request
          </Button>
          <Button
            startIcon={<RefreshIcon />}
            onClick={load}
            disabled={loading}
            sx={{ color: NURSE_COLORS.primary, border: `1px solid ${NURSE_COLORS.border}`, borderRadius: 20, textTransform: "none" }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}`, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {["raised", "approved", "transferred", "rejected", "cancelled"].map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      <Paper sx={{ p: 2, borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}` }}>
        <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: NURSE_COLORS.pageBg }}>
              <TableCell>Admission</TableCell>
              <TableCell>Patient</TableCell>
              <TableCell>Ward</TableCell>
              <TableCell>From</TableCell>
              <TableCell>To</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Consultant</TableCell>
              <TableCell>Payment Details</TableCell>
              <TableCell>Requested By</TableCell>
              <TableCell>Requested At</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={12} align="center" sx={{ color: NURSE_COLORS.textSecondary }}>
                  No transfer requisitions
                </TableCell>
              </TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r.id}>
                <TableCell>{r.admission_id}</TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{r.patient_name || "-"}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {[r.patient_uhid, r.patient_age != null ? `${r.patient_age}${r.patient_gender || ""}` : null]
                      .filter(Boolean).join(" · ") || "-"}
                  </Typography>
                </TableCell>
                <TableCell>{r.ward_name || r.ward_code || "-"}</TableCell>
                <TableCell>{r.from_bed_code}</TableCell>
                <TableCell>{r.to_bed_code}</TableCell>
                <TableCell>{r.transfer_type}</TableCell>
                <TableCell>
                  <Chip size="small" label={r.status} color={STATUS_COLORS[r.status] || "default"} />
                </TableCell>
                <TableCell>{r.admitting_doctor_name || "-"}</TableCell>
                <TableCell>{r.payer_type || "-"}</TableCell>
                <TableCell>{r.requested_by_name || r.requested_by || "-"}</TableCell>
                <TableCell>{fmt(r.requested_at)}</TableCell>
                <TableCell align="right">
                  {r.status === "raised" && (
                    <>
                      <Button
                        size="small"
                        disabled={busyId === r.id}
                        onClick={() => runAction(r.id, approveTransferRequest, "Requisition approved.")}
                      >
                        Approve
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        disabled={busyId === r.id}
                        onClick={() => setRejectTarget(r.id)}
                      >
                        Reject
                      </Button>
                      <Button
                        size="small"
                        disabled={busyId === r.id}
                        onClick={() => runAction(r.id, cancelTransferRequest, "Requisition cancelled.")}
                      >
                        Cancel
                      </Button>
                    </>
                  )}
                  {r.status === "approved" && (
                    <>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={busyId === r.id}
                        onClick={() => runExecute(r)}
                      >
                        Execute
                      </Button>
                      <Button
                        size="small"
                        disabled={busyId === r.id}
                        onClick={() => runAction(r.id, cancelTransferRequest, "Requisition cancelled.")}
                      >
                        Cancel
                      </Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </Box>
      </Paper>

      <Dialog open={!!rejectTarget} onClose={() => setRejectTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Reject Requisition</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            label="Rejection reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleReject}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      <RaiseTransferRequestModal
        open={raiseOpen}
        onClose={() => setRaiseOpen(false)}
        onRaised={() => { setRaiseOpen(false); load(); }}
      />

      <Dialog open={Boolean(approvalNeeded)} onClose={() => setApprovalNeeded(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Approval Required</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "14px" }}>
            Bed <strong>{approvalNeeded?.bedCode}</strong> requires supervisor
            approval before this transfer can be executed. Raise a request
            now — a supervisor can approve it from the Approvals Center.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalNeeded(null)}>Not Now</Button>
          <Button variant="contained" onClick={handleRaiseApproval}>
            Raise Approval Request
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
